import { useParams, Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useUser } from "../hooks/useAuthCompat";
import { useApi } from "../hooks/useApi";
import { useMuscleSchoolEnroll } from "../hooks/useMuscleSchoolEnroll";
import { AskAboutThis } from "../components/AskAboutThis";
import { TMSLessonHeader } from "../components/muscleschool/TMSLessonHeader";
import { TMSLessonNav } from "../components/muscleschool/TMSLessonNav";
import { TMSLessonSectionsDesktop, TMSLessonSectionsMobile } from "../components/muscleschool/TMSLessonSections";
import { toast } from "sonner";
import { AlertTriangle, Info, ShieldCheck } from "lucide-react";

export default function TMSLesson() {
  const { id } = useParams();
  const api = useApi();
  const { isSignedIn, isLoaded } = useUser();
  const [reflectionText, setReflectionText] = useState("");
  const { enroll, checkingOut: enrolling } = useMuscleSchoolEnroll();
  const restoredRef = useRef(false);
  const lastSavedPctRef = useRef(0);
  const lastSavedAtRef = useRef(0);

  const { data: lesson, error } = useQuery({ queryKey: ["tms-lesson", id, isSignedIn], queryFn: async () => (await api.get(`/school/lessons/${id}`)).data, retry: false });
  const { data: curriculum } = useQuery({ queryKey: ["tms-curriculum", isSignedIn], queryFn: async () => (await api.get("/school/curriculum")).data });
  const { data: dashboard } = useQuery({ queryKey: ["tms-dashboard-progress"], queryFn: async () => (await api.get("/school/dashboard")).data, enabled: !!isSignedIn });
  const { data: savedReflection } = useQuery({
    queryKey: ["tms-lesson-reflection", id],
    queryFn: async () => (await api.get(`/school/lessons/${id}/reflection`)).data,
    enabled: !!isSignedIn && !!lesson,
  });

  useEffect(() => {
    if (savedReflection?.text) setReflectionText(savedReflection.text);
  }, [savedReflection]);

  useEffect(() => { restoredRef.current = false; lastSavedPctRef.current = 0; lastSavedAtRef.current = 0; }, [id]);

  const saveScroll = useMutation({
    mutationFn: (pct) => api.post(`/school/lessons/${id}/scroll`, { scroll_pct: pct }),
  });
  const saveScrollRef = useRef(saveScroll.mutate);
  useEffect(() => { saveScrollRef.current = saveScroll.mutate; });

  useEffect(() => {
    if (!lesson || !isSignedIn || restoredRef.current) return;
    restoredRef.current = true;
    const pct = lesson.my_progress?.scroll_pct || 0;
    if (pct > 2) {
      requestAnimationFrame(() => {
        const max = document.documentElement.scrollHeight - window.innerHeight;
        if (max > 0) window.scrollTo({ top: (pct / 100) * max, behavior: "auto" });
      });
    }
  }, [lesson, isSignedIn]);

  useEffect(() => {
    if (!isSignedIn || !lesson) return;
    let debounce;
    let pendingFlush;
    const computePct = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      if (max <= 0) return null;
      return Math.min(100, Math.max(0, Math.round((window.scrollY / max) * 100)));
    };
    const commit = (pct) => {
      lastSavedPctRef.current = pct;
      lastSavedAtRef.current = Date.now();
      saveScrollRef.current(pct);
    };
    const flushNow = () => {
      clearTimeout(debounce);
      clearTimeout(pendingFlush);
      const pct = computePct();
      if (pct != null && pct !== lastSavedPctRef.current) commit(pct);
    };
    const attemptSave = () => {
      const pct = computePct();
      if (pct == null || Math.abs(pct - lastSavedPctRef.current) < 5) return;
      const sinceLast = Date.now() - lastSavedAtRef.current;
      if (sinceLast >= 8000) { commit(pct); return; }
      clearTimeout(pendingFlush);
      pendingFlush = setTimeout(() => {
        const latest = computePct();
        if (latest != null && latest !== lastSavedPctRef.current) commit(latest);
      }, 8000 - sinceLast);
    };
    const onScroll = () => { clearTimeout(debounce); debounce = setTimeout(attemptSave, 1200); };
    const onHide = () => { if (document.visibilityState === "visible") return; flushNow(); };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("pagehide", flushNow);
    document.addEventListener("visibilitychange", onHide);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("pagehide", flushNow);
      document.removeEventListener("visibilitychange", onHide);
      clearTimeout(debounce);
      clearTimeout(pendingFlush);
    };
  }, [isSignedIn, lesson, id]);

  const complete = useMutation({
    mutationFn: async () => api.post(`/school/lessons/${id}/complete?knowledge_check_passed=true`),
    onSuccess: () => toast.success("Lesson marked complete."),
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not mark lesson complete."),
  });

  const saveReflection = useMutation({
    mutationFn: async () => api.post(`/school/lessons/${id}/reflection`, { text: reflectionText }),
    onSuccess: () => toast.success("Reflection saved to your dashboard."),
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not save reflection."),
  });

  const markComplete = () => {
    if (!isLoaded) return;
    if (!isSignedIn) return toast.error("Sign in to save your progress.");
    complete.mutate();
  };

  const submitReflection = () => {
    if (!isLoaded) return;
    if (!isSignedIn) return toast.error("Sign in to save your reflection.");
    if (!reflectionText.trim()) return toast.error("Write a reflection before saving.");
    saveReflection.mutate();
  };

  const flat = curriculum?.acts?.flatMap((a) => a.modules.flatMap((m) =>
    m.lessons.map((l) => ({ ...l, actTitle: a.title, moduleTitle: m.title })))) || [];
  const idx = flat.findIndex((l) => l.id === id);
  const prevCandidate = idx > 0 ? flat[idx - 1] : null;
  const nextCandidate = idx >= 0 && idx < flat.length - 1 ? flat[idx + 1] : null;
  const prev = prevCandidate && !prevCandidate.locked ? prevCandidate : null;
  const next = nextCandidate && !nextCandidate.locked ? nextCandidate : null;
  const current = flat[idx];
  const progressPct = dashboard?.total_lessons ? Math.round((dashboard.completed_count / dashboard.total_lessons) * 100) : null;

  if (error) {
    return (
      <div className="theme-school min-h-screen flex items-center justify-center text-center px-6" data-testid="tms-lesson-locked-page">
        <div>
          <p className="text-textSecondary mb-4">Enroll in The Muscle School to unlock this lesson.</p>
          <Link to="/themuscleschool" className="underline text-primary">Back to The Muscle School</Link>
        </div>
      </div>
    );
  }
  if (!lesson) return <div className="theme-school min-h-screen" />;

  return (
    <div className="theme-school min-h-screen" data-testid="tms-lesson-page">
      <TMSLessonHeader lesson={lesson} actTitle={current?.actTitle} moduleTitle={current?.moduleTitle} progressPct={progressPct} />

      <div className="max-w-[1100px] mx-auto px-6 py-12 md:py-20 grid grid-cols-1 xl:grid-cols-[1fr_200px] gap-12">
      <div className="max-w-[70ch] mx-auto xl:mx-0 w-full">
        <TMSLessonSectionsMobile lesson={lesson} />
        {lesson.status === "import_pending" ? (
          <div data-testid="tms-lesson-import-pending-banner" className="flex items-start gap-2 border border-foreground/30 bg-foreground/5 text-sm px-4 py-3">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>This lesson's full body is UNRESOLVED — AUTHORITATIVE PARTNER OR ASSET EVIDENCE REQUIRED. Structural placeholder shown.</span>
          </div>
        ) : (
          <>
            {lesson.status === "staged_unreviewed" && (
              <div data-testid="tms-lesson-unreviewed-banner" className="flex items-start gap-2 border border-foreground/20 bg-foreground/5 text-xs px-4 py-3 mb-8">
                <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                <span>MACHINE_EXTRACTED / UNREVIEWED — staged from authoritative source, pending Director review.</span>
              </div>
            )}

            {lesson.prerequisites?.length > 0 && (
              <p className="font-meta text-xs text-textSecondary mb-8">Prerequisites: {lesson.prerequisites.join(", ")}</p>
            )}

            <section id="tms-sec-outcomes" className="scroll-mt-32">
              <p className="font-subtitle text-xs uppercase tracking-[0.2em] text-primary mb-4">Learning Outcomes</p>
              <ul className="list-disc pl-5 space-y-1.5 text-base font-body text-foreground/90">
                {lesson.learning_outcomes.map((o, i) => <li key={i}>{o}</li>)}
              </ul>
            </section>

            <hr className="my-12 border-t border-primary/20 w-1/3 mx-auto" />

            <section id="tms-sec-body"
              className="font-body text-base md:text-lg text-foreground/90 leading-relaxed md:leading-[1.8] scroll-mt-32
                [&>h4]:font-display [&>h4]:text-primary [&>h4]:tracking-tight [&>h4]:mt-12 [&>h4]:mb-4 [&>h4]:text-sm [&>h4]:uppercase [&>h4]:tracking-[0.15em]
                [&>p]:mb-5
                [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:mb-5 [&>ul]:space-y-2"
              dangerouslySetInnerHTML={{ __html: lesson.body_html }} />

            {lesson.key_terms?.length > 0 && (
              <section id="tms-sec-terms" className="relative p-6 my-10 border border-border bg-card/50 scroll-mt-32">
                <p className="font-meta text-[10px] uppercase tracking-widest text-textSecondary mb-4">Key Terms</p>
                <ul className="text-sm md:text-base space-y-2.5 font-body">
                  {lesson.key_terms.map((t, i) => <li key={i}>{t}</li>)}
                </ul>
              </section>
            )}

            {lesson.sources?.length > 0 && (
              <section id="tms-sec-sources" className="mt-10 scroll-mt-32">
                <p className="font-subtitle text-xs uppercase tracking-[0.2em] text-primary mb-4">Cited Evidence</p>
                <ul className="text-xs md:text-sm text-textSecondary list-disc pl-5 space-y-2.5 font-meta">
                  {lesson.sources.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </section>
            )}

            {lesson.reflection_prompts?.length > 0 && (
              <section id="tms-sec-reflection" className="relative p-6 md:p-8 my-10 border-l-[3px] border-primary bg-primary/5 scroll-mt-32" data-testid="tms-lesson-reflection">
                <p className="font-subtitle text-xs uppercase tracking-[0.2em] text-primary mb-4">Reflection</p>
                <ul className="list-disc pl-5 space-y-3 text-sm md:text-base font-body italic text-foreground/85 mb-6">
                  {lesson.reflection_prompts.filter((p) => p.length > 20).map((p, i) => <li key={i}>{p}</li>)}
                </ul>
                <textarea
                  data-testid="tms-lesson-reflection-textarea"
                  value={reflectionText}
                  onChange={(e) => setReflectionText(e.target.value)}
                  placeholder="Write your reflection — saved to your dashboard."
                  rows={4}
                  className="w-full bg-background border border-border p-3 text-sm font-body focus:outline-none focus:border-primary"
                />
                <div className="mt-4 flex flex-wrap gap-3">
                  <button data-testid="tms-lesson-save-reflection-button" onClick={submitReflection} disabled={saveReflection.isPending} className="bg-secondary text-secondary-foreground px-5 py-2 text-sm uppercase tracking-wide border border-border hover:border-primary transition-colors duration-200 disabled:opacity-50">
                    {saveReflection.isPending ? "Saving…" : "Save Reflection"}
                  </button>
                  <button data-testid="tms-lesson-complete-button" onClick={markComplete} className="bg-primary text-primary-foreground px-5 py-2 text-sm uppercase tracking-wide">
                    Mark Lesson Complete
                  </button>
                </div>
              </section>
            )}
          </>
        )}

        {lesson.is_free && !curriculum?.enrolled && (
          <div data-testid="tms-lesson-unlock-cta" className="mt-14 bg-[#1a0a0c] p-8 md:p-10 text-center">
            <p className="font-meta text-xs uppercase tracking-[0.3em] text-[#c9962c] mb-3">This Was Lesson I.1.1</p>
            <p className="text-white/70 mb-6 font-body">106 lessons across 7 Acts are waiting. One payment, lifetime access.</p>
            <button
              data-testid="tms-lesson-unlock-button"
              onClick={enroll}
              disabled={enrolling}
              className="bg-[#c9962c] text-black px-7 py-3.5 text-sm uppercase tracking-wide font-medium hover:bg-[#e0ad3d] transition-colors duration-200 disabled:opacity-50"
            >
              {enrolling ? "Redirecting…" : "Unlock the Complete Curriculum — $497"}
            </button>
            <p className="text-xs text-white/40 flex items-center justify-center gap-1.5 mt-4">
              <ShieldCheck className="w-3.5 h-3.5 text-[#c9962c]" /> Secure checkout · Powered by Stripe
            </p>
          </div>
        )}

        <div className="mt-10">
          <AskAboutThis type="lesson" refId={lesson.id} title={lesson.title} />
        </div>

        <TMSLessonNav prev={prev} next={next} />
      </div>
      <TMSLessonSectionsDesktop lesson={lesson} />
      </div>
    </div>
  );
}
